import { useSendNotification } from "@app/hooks/useNotification";
import { clientFetch } from "@app/lib/egress/client";
import {
  emptyArray,
  getErrorFromResponse,
  useFetcher,
  useSWRWithDefaults,
} from "@app/lib/swr/swr";
import type {
  GetPersonalTaskReceiptsResponseBody,
  GetPersonalTasksResponseBody,
  PersonalTaskActionsBody,
  PersonalTaskReceiptWireType,
  PostPersonalTaskActionsResponseBody,
  PostPersonalTaskResponseBody,
} from "@app/types/api/me/tasks";
import type { PersonalTaskWireType } from "@app/types/project_task";
import { Err, Ok, type Result } from "@app/types/shared/result";
import { normalizeError } from "@app/types/shared/utils/error_utils";
import type { LightWorkspaceType } from "@app/types/user";
import { useCallback } from "react";
import type { Fetcher } from "swr";

export function buildPersonalTasksUrl(workspaceId: string): string {
  return `/api/w/${workspaceId}/me/tasks`;
}

export function usePersonalTasks({
  owner,
  disabled,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const tasksFetcher: Fetcher<GetPersonalTasksResponseBody> = fetcher;

  const { data, error, isValidating, mutate } = useSWRWithDefaults(
    buildPersonalTasksUrl(owner.sId),
    tasksFetcher,
    { disabled }
  );

  return {
    tasks: data?.tasks ?? emptyArray<PersonalTaskWireType>(),
    requestedTasks: data?.requested ?? emptyArray<PersonalTaskWireType>(),
    isTasksLoading: !disabled && !error && !data,
    isTasksValidating: isValidating,
    isTasksError: !!error,
    mutateTasks: mutate,
  };
}

export function usePersonalTaskReceipts({
  owner,
  taskId,
  disabled,
}: {
  owner: LightWorkspaceType;
  taskId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const receiptsFetcher: Fetcher<GetPersonalTaskReceiptsResponseBody> = fetcher;

  const { data, error } = useSWRWithDefaults(
    taskId ? `${buildPersonalTasksUrl(owner.sId)}/${taskId}/receipts` : null,
    receiptsFetcher,
    { disabled }
  );

  return {
    receipts: data?.receipts ?? emptyArray<PersonalTaskReceiptWireType>(),
    isReceiptsLoading: !disabled && !!taskId && !error && !data,
    isReceiptsError: !!error,
  };
}

/** Quick-add on My Tasks: one POST, then the list revalidates. */
export function useCreatePersonalTask({
  owner,
}: {
  owner: LightWorkspaceType;
}) {
  const sendNotification = useSendNotification();
  const { mutateTasks } = usePersonalTasks({ owner, disabled: true });

  return useCallback(
    async (
      text: string,
      dueOn: string | null = null
    ): Promise<Result<PersonalTaskWireType, Error>> => {
      try {
        const res = await clientFetch(buildPersonalTasksUrl(owner.sId), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text, dueOn }),
        });

        if (!res.ok) {
          const errorData = await getErrorFromResponse(res);
          sendNotification({
            type: "error",
            title: "Failed to add task",
            description: errorData.message,
          });
          return new Err(new Error(errorData.message));
        }

        const responseData: PostPersonalTaskResponseBody = await res.json();
        await mutateTasks();

        return new Ok(responseData.task);
      } catch (e) {
        const error = normalizeError(e);
        sendNotification({
          type: "error",
          title: "Failed to add task",
          description: error.message,
        });
        return new Err(error);
      }
    },
    [mutateTasks, owner.sId, sendNotification]
  );
}

const ACTION_FAILURE_TITLES: Record<PersonalTaskActionsBody["action"], string> =
  {
    accept: "Failed to accept task",
    dismiss: "Failed to dismiss task",
    undo_dismiss: "Failed to restore task",
    undo_start: "Failed to undo the start",
    complete: "Failed to complete task",
    reopen: "Failed to reopen task",
    snooze: "Failed to snooze task",
    set_due: "Failed to change the due date",
    pin: "Failed to pin task",
    unpin: "Failed to unpin task",
    delete: "Failed to delete task",
    set_status: "Failed to move task",
    reorder: "Failed to reorder tasks",
  };

/**
 * Every Home action on a personal task goes through the one `/actions`
 * endpoint, so there is one place that posts, notifies on failure and
 * revalidates the list.
 */
export function usePersonalTaskActions({
  owner,
}: {
  owner: LightWorkspaceType;
}) {
  const sendNotification = useSendNotification();
  const { mutateTasks } = usePersonalTasks({ owner, disabled: true });

  return useCallback(
    async (
      body: PersonalTaskActionsBody
    ): Promise<Result<PostPersonalTaskActionsResponseBody, Error>> => {
      try {
        const res = await clientFetch(
          `${buildPersonalTasksUrl(owner.sId)}/actions`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        );

        if (!res.ok) {
          const errorData = await getErrorFromResponse(res);
          sendNotification({
            type: "error",
            title: ACTION_FAILURE_TITLES[body.action],
            description: errorData.message,
          });
          return new Err(new Error(errorData.message));
        }

        const responseData: PostPersonalTaskActionsResponseBody =
          await res.json();
        await mutateTasks();

        return new Ok(responseData);
      } catch (e) {
        const error = normalizeError(e);
        sendNotification({
          type: "error",
          title: ACTION_FAILURE_TITLES[body.action],
          description: error.message,
        });
        return new Err(error);
      }
    },
    [mutateTasks, owner.sId, sendNotification]
  );
}
